import { ArrowLeftOutlined, MailOutlined } from '@ant-design/icons';
import { Button, Form, Input, message } from 'antd';
import { sendPasswordResetEmail } from 'firebase/auth';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { SEO_DEFAULTS, PAGE_SEO } from '../../utils/seoConfig';
import { auth } from '../../firebase';
import { brandMarkSrc } from '../../utils/brandMark';
import './AuthForm.css';

const ForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [form] = Form.useForm();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  // Reward early (same model as Login/SignUp): once a field HAS an error it
  // re-validates on every keystroke so the error clears the instant it's fixed.
  const onValuesChange = (changedValues) => {
    const errored = Object.keys(changedValues).filter(
      (name) => form.getFieldError(name).length > 0
    );
    if (errored.length) form.validateFields(errored);
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, values.email);
      setSentEmail(values.email);
      setEmailSent(true);
      message.success('Password reset email sent!');
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/user-not-found') {
        message.error('No account found with this email address');
      } else if (error.code === 'auth/invalid-email') {
        message.error('Invalid email address');
      } else {
        message.error('Failed to send reset email. Please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const forgotPasswordSEO = {
    title: 'Reset Password - CellCog',
    description: 'Reset your CellCog password. Enter your email address and we\'ll send you a link to reset your password.',
  };

  return (
    <>
      <Helmet>
        <title>{forgotPasswordSEO.title}</title>
        <meta name="description" content={forgotPasswordSEO.description} />
        <link rel="canonical" href={`${SEO_DEFAULTS.url}/forgot-password`} />
        <meta property="og:title" content={forgotPasswordSEO.title} />
        <meta property="og:description" content={forgotPasswordSEO.description} />
        <meta property="og:url" content={`${SEO_DEFAULTS.url}/forgot-password`} />
        <meta name="twitter:title" content={forgotPasswordSEO.title} />
        <meta name="twitter:description" content={forgotPasswordSEO.description} />
        <meta name="robots" content="noindex" />
      </Helmet>
      
      <div className="auth-page-container">
        <div className="auth-form-wrapper">
          <div className="auth-form-card">
            {!emailSent ? (
              <>
                <div className="auth-form-header">
                  <div className="auth-form-logo">
                    <img
                      src={brandMarkSrc(isDarkMode)}
                      alt="CellCog Logo"
                      style={{
                        width: '64px',
                        height: '64px',
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                  <h1 className="auth-form-title">Reset your password</h1>
                  <p className="auth-form-subtitle">
                    Enter your email and we'll send you a reset link
                  </p>
                </div>

                <Form 
                  form={form}
                  name="forgot-password" 
                  onFinish={onFinish} 
                  onValuesChange={onValuesChange}
                  layout="vertical"
                  className="auth-form"
                  requiredMark={false}
                  validateTrigger="onBlur"
                >
                  <Form.Item
                    name="email"
                    rules={[
                      // Same validation model as Login/SignUp (Nitish Aug 24):
                      // required = submit-only, format = blur on non-empty.
                      { required: true, message: 'Please enter your email', validateTrigger: 'onSubmit' },
                      { type: 'email', message: 'Please enter a valid email', validateTrigger: 'onBlur' }
                    ]}
                  >
                    <Input 
                      placeholder="Email address" 
                      size="large"
                      autoComplete="email"
                    />
                  </Form.Item>

                  <div className="auth-form-buttons">
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      loading={loading} 
                      block
                      size="large"
                    >
                      Send Reset Link
                    </Button>
                    
                    <Button 
                      onClick={handleBackToLogin}
                      icon={<ArrowLeftOutlined />}
                      block
                      size="large"
                      className="auth-google-btn"
                    >
                      Back to Login
                    </Button>
                  </div>

                </Form>

                <div className="auth-form-links">
                  <span className="auth-form-link">Don't have an account?</span>
                  <Link to="/signup" className="auth-form-link auth-form-link-primary">
                    Sign Up
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="auth-form-header">
                  <div className="auth-form-logo">
                    <MailOutlined style={{ fontSize: '48px' }} />
                  </div>
                  <h1 className="auth-form-title">Check your email</h1>
                  <p className="auth-form-subtitle">
                    We've sent a password reset link to<br />
                    <strong>{sentEmail}</strong>
                  </p>
                </div>

                <div className="auth-form-buttons" style={{ marginTop: '32px' }}>
                  <Button 
                    type="primary"
                    onClick={handleBackToLogin}
                    block
                    size="large"
                  >
                    Back to Login
                  </Button>
                </div>

                <div className="auth-form-links" style={{ marginTop: '24px' }}>
                  <span className="auth-form-link">Didn't receive the email?</span>
                  <button 
                    onClick={() => setEmailSent(false)}
                    className="auth-form-link auth-form-link-primary"
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer',
                      padding: 0,
                      font: 'inherit'
                    }}
                  >
                    Try again
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
